import type { Release, ReleaseGroup } from "../api/releases";
import {
  RELEASE_GROUP_OPTION_PREFIX,
  RELEASE_GROUP_OPTION_TYPE,
} from "../constants/releases";

/**
 * A release as the filter/picker machinery wants it: a flat option carrying
 * enough of its group to be re-nested by `CheckboxFilter`'s `release` branch.
 *
 * The group metadata rides on every leaf rather than arriving as a separate
 * tree because that is the shape `addOptionsToFilters` produces from a
 * context key, and the same shape the backend would send inline. Keeping one
 * shape means the picker and the ideas-list filter share a branch instead of
 * forking one.
 */
export interface ReleaseOption {
  _id: string;
  value: string;
  label: string;
  name: string;
  releaseGroupId: string;
  releaseGroupName: string;
}

/** The synthetic parent row a group is rendered as. Never a submitted value. */
export interface ReleaseGroupOption {
  value: string;
  label: string;
  name: string;
  type: typeof RELEASE_GROUP_OPTION_TYPE;
  childComponents: ReleaseOption[];
}

export const releaseGroupOptionValue = (groupId: string): string =>
  `${RELEASE_GROUP_OPTION_PREFIX}${groupId}`;

export const isReleaseGroupOptionValue = (value: string): boolean =>
  typeof value === "string" &&
  value.toLowerCase().startsWith(RELEASE_GROUP_OPTION_PREFIX);

/**
 * Dates first, insertion order second — the only two orderings a release has now that
 * `PUT /releases/reorder` is gone and nothing hand-sorts the list.
 *
 * 1. `endDate ?? startDate`, newest first. For a Jira-sourced release the end date is the
 *    version's `releaseDate`; for a ProductBoard one it is `timeframe.endDate ?? targetDate`.
 * 2. `_id`, highest first. ObjectId is monotonic, so this is insertion order with the most
 *    recently added release on top, consistent with dates-newest-first above. This is the
 *    same tiebreak `bagel-core` applies (`{ sortDate: -1, _id: -1 }`); it replaced a stored
 *    `index` counter that no longer exists.
 * 3. Name, so the order is stable rather than dependent on fetch order.
 *
 * Undated releases always sort below dated ones: an unscheduled release is not "older" than
 * one that shipped in 2019, it simply has no place on the timeline.
 */
interface ReleaseRecencyFields {
  _id?: string;
  name?: string;
  startDate?: string;
  endDate?: string;
}

const parseTime = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const time = Date.parse(String(value));
  return Number.isNaN(time) ? null : time;
};

const shipTime = (release: ReleaseRecencyFields): number | null =>
  parseTime(release.endDate) ?? parseTime(release.startDate);

/** Newest first. Dated releases always precede undated ones. */
export const compareReleasesByRecency = (
  a: ReleaseRecencyFields,
  b: ReleaseRecencyFields
): number => {
  const aTime = shipTime(a);
  const bTime = shipTime(b);
  if (aTime !== null && bTime !== null) {
    if (aTime !== bTime) return bTime - aTime;
  } else if (aTime !== null) {
    return -1;
  } else if (bTime !== null) {
    return 1;
  }

  const aId = String(a._id || "");
  const bId = String(b._id || "");
  if (aId !== bId) return bId.localeCompare(aId);

  return String(a.name || "").localeCompare(String(b.name || ""));
};

/** The bucket an untagged release renders under. Never a submitted value. */
export const UNGROUPED_RELEASE_GROUP_ID = "ungrouped";
export const UNGROUPED_RELEASE_GROUP_NAME = "Ungrouped";

/**
 * Flat options, in group order then most-recent-release-first order.
 *
 * A group is a tag, so a release carrying several appears **once per group** - the same
 * release id under two parents, which every consumer already de-duplicates by id
 * (`toReleaseIds`, and the checkbox selection sets). A release carrying none appears once
 * under a synthetic "Ungrouped" bucket rather than vanishing: untagged is the normal state
 * for anything synced from Jira, where the project is recorded on the external ref instead of
 * being turned into a group.
 *
 * A tag pointing at a group that no longer exists is dropped, and if that leaves the release
 * with nothing it falls into Ungrouped - so a stale cache entry hides a row from no one.
 */
export const buildReleaseOptions = (
  releases: Release[],
  groups: ReleaseGroup[]
): ReleaseOption[] => {
  const groupById = new Map(groups.map((group) => [group._id, group]));
  const sorted = [...releases].sort(compareReleasesByRecency);

  const toOption = (
    release: Release,
    releaseGroupId: string,
    releaseGroupName: string
  ): ReleaseOption => ({
    _id: release._id,
    value: release._id,
    label: release.name,
    name: release.name,
    releaseGroupId,
    releaseGroupName,
  });

  const grouped = groups.flatMap((group) =>
    sorted
      .filter((release) => (release.releaseGroupIds || []).includes(group._id))
      .map((release) => toOption(release, group._id, group.name))
  );

  const ungrouped = sorted
    .filter(
      (release) =>
        !(release.releaseGroupIds || []).some((groupId) => groupById.has(groupId))
    )
    .map((release) =>
      toOption(release, UNGROUPED_RELEASE_GROUP_ID, UNGROUPED_RELEASE_GROUP_NAME)
    );

  return [...grouped, ...ungrouped];
};

/**
 * Re-nests flat options under their synthetic group parents. Shared by
 * `CheckboxFilter`'s `release` branch and by anything that needs to know which
 * releases belong to a single-select group.
 */
export const groupReleaseOptions = (
  options: any[]
): ReleaseGroupOption[] => {
  const byGroup = new Map<string, ReleaseGroupOption>();

  (options || []).forEach((option: any) => {
    if (!option) return;
    const groupId = String(option.releaseGroupId || "");
    if (!groupId) return;

    if (!byGroup.has(groupId)) {
      byGroup.set(groupId, {
        value: releaseGroupOptionValue(groupId),
        label: option.releaseGroupName || "Releases",
        name: option.releaseGroupName || "Releases",
        type: RELEASE_GROUP_OPTION_TYPE,
        childComponents: [],
      });
    }
    byGroup.get(groupId)?.childComponents.push(option);
  });

  return Array.from(byGroup.values());
};

/**
 * Re-derives the synthetic group ticks a stored selection implies.
 *
 * A group tick is never stored: `toReleaseIds` strips `release-group:<id>` on
 * the way to the server, because a group is a label rather than a value the
 * endpoint accepts. So on the way back in it has to be worked out again, or a
 * group whose every release is selected renders unticked the moment the picker
 * re-seeds — and its "select all" no longer matches what is actually saved.
 *
 * The tick is a pure function of the children: present when all of them are
 * selected, absent otherwise. Any group value already in `checkedValues` is
 * dropped and recomputed, so a stale one left behind by unticking a child
 * cannot survive.
 *
 * Derive it from the **complete** option list, never a searched one: a group
 * half-hidden by a search box is not a half-selected group.
 */
export const withGroupTicks = (
  checkedValues: string[],
  groupedOptions: ReleaseGroupOption[]
): string[] => {
  const realValues = (checkedValues || []).filter(
    (value) => !isReleaseGroupOptionValue(String(value))
  );
  const selected = new Set(
    realValues.map((value) => String(value).toLowerCase())
  );

  const groupTicks = (groupedOptions || [])
    .filter((group) => {
      const children = group?.childComponents || [];
      return (
        children.length > 0 &&
        children.every((child) =>
          selected.has(String(child.value).toLowerCase())
        )
      );
    })
    .map((group) => String(group.value));

  return [...realValues, ...groupTicks];
};

/**
 * Keeps only real release ids, case-insensitively, and restores their stored
 * casing. `CheckboxFilter` lowercases every option value and cascades a group
 * tick down to its children — which leaves the synthetic group value sitting
 * in the selection. Submitting that to `PUT /product-items/:id/releases` would
 * be a 400, so every set the picker hands back goes through here first.
 *
 * An id that matches no known release is dropped, deliberately: the server
 * `$pull`s a deleted release out of every product item, so such an id is
 * already gone server-side and re-submitting it would be rejected. It stays
 * *visible* as an unknown chip until the ideas list refetches — see
 * `ReleaseChips` — but it is never written back.
 *
 * `retainIds` is the exception, and it exists because the write is a **full
 * replacement**. `options` is built from the picker's page of releases, which
 * is capped (`PICKER_PAGE_SIZE`); past that cap an id can be absent from
 * `options` and still be perfectly real. `CheckboxFilter` keeps a selected
 * value that has no rendered checkbox, so such an id arrives here on every
 * toggle — and dropping it would delete an assignment the user never touched
 * and cannot see. Ids listed in `retainIds` therefore survive with the casing
 * they were stored in.
 *
 * Pass it **only when the list is actually truncated** (`releasesTruncated`).
 * On a complete list an unknown id really is a deleted one, and retaining it
 * would trade today's silent drop for a 400 that blocks every later edit to
 * the row.
 */
export const toReleaseIds = (
  selectedValues: string[],
  options: ReleaseOption[],
  retainIds: string[] = []
): string[] => {
  const byLowerId = new Map(
    options.map((option) => [option.value.toLowerCase(), option.value])
  );
  (retainIds || []).forEach((id) => {
    const value = String(id);
    const key = value.toLowerCase();
    if (!key || byLowerId.has(key)) return;
    byLowerId.set(key, value);
  });

  const seen = new Set<string>();
  const ids: string[] = [];

  (selectedValues || []).forEach((value) => {
    const id = byLowerId.get(String(value).toLowerCase());
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });

  return ids;
};
